import React from 'react';
import styled from 'styled-components';
import AuthPanel from './AuthPanel';

export const PRIVACY_CONTACT_EMAIL = 'tanvi.agrawal.000@gmail.com';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Content = styled.article`
  max-width: 720px;
  margin: 0 auto 40px;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.6;

  h1 {
    margin: 0 0 8px;
    font-size: 2rem;
  }

  h2 {
    margin: 28px 0 8px;
    font-size: 1.15rem;
  }

  p, li {
    font-size: 0.98rem;
  }

  a {
    color: #d7c4ff;
  }
`;

const Updated = styled.p`
  color: rgba(255, 255, 255, 0.55);
  margin-top: 0;
`;

function PrivacyPage({
  onBack,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  onNavigateToAbout,
  canCreatePost,
  canReview
}) {
  return (
    <Container>
      <TopBar>
        <BackButton type="button" onClick={onBack}>Back</BackButton>
        <AuthPanel
          onNavigateToSaved={onNavigateToSaved}
          onNavigateToRecommendations={onNavigateToRecommendations}
          onNavigateToCreate={onNavigateToCreate}
          onNavigateToMyUploads={onNavigateToMyUploads}
          onNavigateToReview={onNavigateToReview}
          onNavigateToAbout={onNavigateToAbout}
          canCreatePost={canCreatePost}
          canReview={canReview}
        />
      </TopBar>
      <Content>
        <h1>Privacy Policy</h1>
        <Updated>Last updated October 14, 2026</Updated>

        <p>
          RealityCheck helps you find immersive experiences. This page says what we store, what other people can see, and how to change or remove it.
        </p>

        <h2>What we collect and why</h2>
        <p>We collect this information to run the site, personalize recommendations, show ratings and reviews, and improve RealityCheck.</p>
        <ul>
          <li>Account: if you sign up with email, we store your email, password (kept by Google Firebase Authentication, not by us in plain text), and the display name you choose. If you continue with Google, we also receive the name, email, and profile photo Google shares. We store your account id.</li>
          <li>Ratings and reviews: the star score you pick, the time you created or changed it, and any review text you write.</li>
          <li>Saved experiences: which experiences you save, and when.</li>
          <li>Recommendations: these are built on this device from the experiences you saved. We do not store a separate recommendation profile.</li>
          <li>Share an Experience: if you recommend an experience, we store your name, email, account id, profile photo when you have one, the experience name, link, and any note you add. A draft of that form is saved while you are signed in. If you join the creator waitlist, we store your name, email, the experience link, and your account id when you are signed in. The full creator form, when it is open, also stores the experience details you enter and a hero image.</li>
          <li>On this device: whether you want to stay signed in, the email you last used when that option is on, that you have seen the intro, the page and homepage filters for this browser session, and when you dismiss the save prompt. Firebase also keeps your sign-in session in the browser.</li>
        </ul>
        <p>
          Personalize Your Feed is not collecting answers. The button shows “Coming Soon” and does not save a quiz.
        </p>

        <h2>Activity data</h2>
        <p>
          We do not currently send usage data to Google Analytics or Firebase Analytics. The site has code that could record a sign-in, including your account id and the time, but that only runs if an analytics measurement ID is configured. It is not configured, and the session-length helpers in the code are not used.
        </p>
        <p>
          What we do keep about how you use the site is the ratings, reviews, and saves described above, plus the filters and page you were on during this browser session.
        </p>

        <h2>What other people can see</h2>
        <p>
          The average star rating and the number of ratings are public. A star rating with no written review is included in that average. It is not listed on its own, and no name is stored for it.
        </p>
        <p>
          If you write a review, the stars, the review text, and your display name are visible to everyone. Your email is not shown.
        </p>
        <p>We never sell your personal information. Private info like your email is never shown publicly or given to other users.</p>

        <h2>Who processes the data</h2>
        <p>
          Account, ratings, reviews, saves, submissions, drafts, and uploaded images are stored and processed by Google Firebase (Authentication, Firestore, Cloud Storage, and Cloud Functions).
        </p>

        <h2>Changing or deleting your information</h2>
        <ul>
          <li>Display name: open Account in the signed-in menu, edit the name, and save. Written reviews you already posted use the new name.</li>
          <li>Star rating: open the experience and pick a different star, tap the selected star again, or use Clear rating. Clearing the rating removes it from the public average. If you also wrote a review, that public review is removed too.</li>
          <li>Review text: edit the box and save, or clear the text and save. Clearing the text removes the public review. The star rating stays in the average.</li>
          <li>Saves: unsave an experience from the site. That removes it from your saved list.</li>
        </ul>
        <p>
          There is no button yet to delete your whole account. Email{' '}
          <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>
          {' '}and ask us to delete a rating, a review, or your account. We will also delete the saved list and submission records tied to that account when you ask us to delete the account.
        </p>
        <p>We may remove reviews that are spam, abusive, or share personal information.</p>

        <h2>Children</h2>
        <p>RealityCheck is not intended for children under 13.</p>
      </Content>
    </Container>
  );
}

export default PrivacyPage;
